import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { MessageCircle, ArrowRight } from "lucide-react";
import { serviceList } from "../data/mock";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import CTASection from "../components/CTASection";
import { useLanguage } from "../i18n/LanguageContext";
import { usePublicContent } from "../i18n/PublicContentContext";

export default function Services() {
  const { t, lang } = useLanguage();
  const pub = usePublicContent();
  const sp = t.servicesPage;

  const svcMap = React.useMemo(() => {
    const m = {};
    (pub.services_meta || []).forEach((x) => { m[x.slug] = x; });
    return m;
  }, [pub.services_meta]);
  const pickL = (o, en, hi) => (lang === "hi" ? (o?.[hi] || o?.[en] || "") : (o?.[en] || ""));
  const svcTitle = (slug) => pickL(svcMap[slug], "title_en", "title_hi") || t.services[slug].title;
  const svcShort = (slug) => pickL(svcMap[slug], "short_en", "short_hi") || t.services[slug].short;
  const activeServices = serviceList.filter((s) => svcMap[s.slug]?.active !== false);

  return (
    <div className="bg-white">
      <Helmet>
        <title>Hotel Management Services | Superhost Cloud</title>
        <meta name="description" content="Explore Superhost Cloud's 10 hotel management services." />
      </Helmet>

      <section className="relative pt-36 pb-16 lg:pb-20">
        <div className="absolute inset-0 bg-gradient-to-b from-[#F4F6FA] to-white" />
        <div className="absolute top-10 right-10 w-80 h-80 rounded-full bg-[#E84B1C]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="max-w-3xl">
            <span className="font-head text-[#E84B1C] font-semibold tracking-wide uppercase text-sm">{sp.eyebrow}</span>
            <h1 className="mt-3 font-head text-4xl md:text-6xl font-extrabold leading-tight">
              <span className="text-[#E84B1C]">{sp.title1}</span>{" "}
              <span className="text-[#12284B]">{sp.title2}</span>
            </h1>
            <p className="mt-5 text-black/70 text-lg max-w-2xl">{sp.paragraph}</p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {activeServices.map((s) => {
              const Icon = s.icon;
              return (
                <StaggerItem key={s.slug}>
                  <div className="group h-full flex flex-col bg-[#17205C] border border-white/10 hover:border-[#E84B1C]/50 rounded-2xl p-7 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-[#12284B]/20">
                    <div className="h-14 w-14 rounded-xl bg-[#E84B1C]/20 flex items-center justify-center group-hover:bg-[#E84B1C] transition-colors duration-300">
                      <Icon size={26} className="text-[#E84B1C] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="mt-5 font-head text-xl font-semibold text-white">{svcTitle(s.slug)}</h3>
                    <p className="mt-2.5 text-white/70 text-sm leading-relaxed flex-1">{svcShort(s.slug)}</p>
                    <div className="mt-6 flex items-center gap-3">
                      {s.detail ? (
                        <Link
                          to={`/services/${s.slug}`}
                          className="inline-flex items-center gap-1.5 bg-[#E84B1C] hover:bg-[#F0673E] text-white font-head font-semibold text-sm px-5 py-2.5 rounded-full transition-all duration-300 hover:scale-105"
                        >
                          {sp.learnMore} <ArrowRight size={16} />
                        </Link>
                      ) : (
                        <a
                          href={pub.waLink(`Hi Superhost Cloud, I'm interested in ${t.services[s.slug].title}.`)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 bg-[#E84B1C] hover:bg-[#F0673E] text-white font-head font-semibold text-sm px-5 py-2.5 rounded-full transition-all duration-300 hover:scale-105"
                        >
                          <MessageCircle size={16} /> {sp.enquire}
                        </a>
                      )}
                    </div>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      <CTASection title={t.cta.servicesTitle} subtitle={t.cta.servicesSubtitle} />
    </div>
  );
}
